// Production camera scene scanner adapter.
//
// This is the adapter Stage wires up for the production build. Unlike the
// removed IndexedDB dev adapter it contains NO IndexedDB, NO persistent
// storage, and NO backend of its own — session/save state lives in a
// module-level Map for the current browser session only (resets on reload),
// exactly like the in-memory stub. Persisting saves to a first-party cloud is
// the owner's concern behind this same interface.
//
// Scanning is delegated to an injected ScanController (the engine's
// SceneScanController), which activates the in-scene Video scanner and resolves
// the stabilized code. Passing a `preferredCode` bypasses the camera and opens
// that code directly — used by unit tests so the full session flow can be
// exercised without hardware.
//
// It is engine-internal: gameplay code must never import it directly. Gameplay
// talks only to the public StickerService, which depends on the
// StickerScannerAdapter port.

import type {
  AdapterScanRequest,
  AdapterScanSession,
  StickerScannerAdapter,
} from '../sticker_adapter';
import type {
  JsonObject,
  ProjectTag,
  StickerCode,
  StickerCodeStatus,
} from '../sticker_types';
import { StickerError, UNASSIGNED_TAG } from '../sticker_types';
import { deepCloneJson } from './clone';
import type { ScanController } from './scan_controller';

export interface CameraSceneScannerAdapterOptions {
  /**
   * Engine-provided controller that activates the in-scene camera scanner and
   * resolves the stabilized code. Omit only in `preferredCode`-only contexts
   * (e.g. isolated tests); a live camera scan without a controller throws
   * `unsupported`.
   */
  controller?: ScanController;
}

interface StoredSession {
  sessionId: string;
  code: StickerCode;
  saveId: string;
}

// Browser-session scoped. NOT persisted — no IndexedDB, no localStorage.
const sessionStore = new Map<string, StoredSession>();
const saveStore = new Map<string, JsonObject>();
let sessionCounter = 0;

function saveIdForCode(code: StickerCode): string {
  return `save-${code}`;
}

// Dev-fixture project tags for the in-memory production stand-in. A first-party
// cloud adapter resolves the real tag from the batch/project catalog (see the
// server-side resolution in src/app/api/codes/_lib/store.ts); this local
// adapter has no backend, so it maps a few fixture codes and defaults every
// other code to the Unassigned tag — the same non-destructive default the
// service applies to legacy/untagged results.
const TAG_BY_CODE: Record<StickerCode, ProjectTag> = {
  '1000000001': { id: 'series-alpha', name: 'Series Alpha' },
  '1000000002': { id: 'series-alpha', name: 'Series Alpha' },
  '1000000003': { id: 'series-beta', name: 'Series Beta' },
};

function tagForCode(code: StickerCode): ProjectTag {
  const tag = TAG_BY_CODE[code];
  return tag ? { ...tag } : { ...UNASSIGNED_TAG };
}

function seedSave(code: StickerCode): JsonObject {
  return {
    playerName: 'Guest',
    score: 0,
    tokens: [],
    level: 1,
    checkpoint: 'start',
    linkedTo: code,
  };
}

export class CameraSceneScannerAdapter implements StickerScannerAdapter {
  readonly id = 'camera';

  private readonly controller?: ScanController;

  constructor(options: CameraSceneScannerAdapterOptions = {}) {
    this.controller = options.controller;
  }

  async scan(request: AdapterScanRequest): Promise<AdapterScanSession> {
    if (request.signal?.aborted) {
      throw new StickerError('cancelled', 'Scan cancelled');
    }

    // A preferredCode opens a specific sticker directly (dev tester / tests) and
    // never brings up the camera. Otherwise we run a real in-scene camera scan
    // through the injected controller.
    let code: StickerCode;
    if (request.preferredCode) {
      code = request.preferredCode;
    } else if (this.controller) {
      code = await this.controller.scan(request);
    } else {
      throw new StickerError(
        'unsupported',
        'No scan controller configured; scanning is unavailable in this context.'
      );
    }

    const saveId = saveIdForCode(code);
    let save = saveStore.get(saveId);
    if (!save) {
      save = seedSave(code);
      saveStore.set(saveId, save);
    }

    const sessionId = `camera-${++sessionCounter}`;
    sessionStore.set(sessionId, { sessionId, code, saveId });

    // A scanned code has established/retrieved its save game, so it is `used` —
    // matching the first-party server response contract.
    const status: StickerCodeStatus = 'used';

    return {
      sessionId,
      code,
      saveGameId: saveId,
      status,
      tag: tagForCode(code),
      linkedCodes: [],
      save: deepCloneJson(save),
    };
  }

  async readSave(sessionId: string): Promise<JsonObject> {
    const stored = this.require(sessionId);
    const save = saveStore.get(stored.saveId);
    if (!save) {
      throw new StickerError('internal', `Missing save: ${stored.saveId}`);
    }
    return deepCloneJson(save);
  }

  async writeSave(sessionId: string, data: JsonObject): Promise<void> {
    const stored = this.require(sessionId);
    saveStore.set(stored.saveId, deepCloneJson(data));
  }

  async getLinkedCodes(sessionId: string): Promise<StickerCode[]> {
    this.require(sessionId);
    return [];
  }

  async endSession(sessionId: string): Promise<void> {
    // Runtime-only mapping; the save is intentionally left in the session store
    // so re-scanning the same code restores it for the browser session.
    sessionStore.delete(sessionId);
  }

  // Production adapter exposes no demo fixtures.

  private require(sessionId: string): StoredSession {
    const stored = sessionStore.get(sessionId);
    if (!stored) {
      throw new StickerError('not_scanning', `Unknown session: ${sessionId}`);
    }
    return stored;
  }
}
